import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tool, FileText, PlusCircle } from "react-feather";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import toast from "react-hot-toast";
import { useGetAmcServiceDueQuery, useCreateVisitFromScheduleMutation } from "../../amc/amcApi";
import DashboardSectionCard from "../components/DashboardSectionCard";
import { paths } from "../../../routes/paths";
import { URGENCY, fmtDate, UrgencyStrip, UrgencySummary } from "./amcUrgency";

const INITIAL_ROWS = 6;

// A job in one of these states is with a technician - somebody is already
// going, so the dashboard has nothing left to ask of the office.
const WITH_TECHNICIAN = ["ASSIGNED", "ONGOING"];

/*
  Two rules added 2026-09-21, both the user's, both applied HERE and not in the
  proc - the same list feeds the AMC screens, where the full picture is wanted.

  1. "if a pending amc service is assigned to tech we dont need to show it in
     the dashboard". A contract drops off as soon as ANY of its pending visits
     has a job that is with a technician: that one trip services the site, so
     its older missed visits stop being a separate call to action too. A job
     merely raised (NOT_STARTED / ON_HOLD) still shows - nobody is going yet.

  2. One row per CONTRACT, not per visit. "why too many repeatation for salim
     c/o": a contract entered on 9 Sep but dated from 1 March gets March and
     June visits that were already in the past the day it was saved, so they
     are MISSED for ever and the customer filled three rows. The most overdue
     visit leads (it sets the colour and the day count) and the rest become a
     "+2 more visits pending" note on the same row.
*/
const toContractRows = (rows) => {
  const byContract = new Map();
  rows.forEach((r) => {
    if (!byContract.has(r.contractRecordGuid)) byContract.set(r.contractRecordGuid, []);
    byContract.get(r.contractRecordGuid).push(r);
  });
  return [...byContract.values()]
    .filter((visits) => !visits.some((v) => v.workNumber && WITH_TECHNICIAN.includes(v.technicalWorkStatus)))
    .map((visits) => {
      // The proc already sorts most-overdue first, so the first is the lead;
      // but a visit that has a job raised is the better one to link to.
      const lead = visits[0];
      const withJob = visits.find((v) => v.workNumber);
      return {
        ...lead,
        workNumber: withJob?.workNumber ?? null,
        technicalWorkRecordGuid: withJob?.technicalWorkRecordGuid ?? null,
        technicalWorkStatus: withJob?.technicalWorkStatus ?? null,
        otherVisits: visits.slice(1),
      };
    });
};

/**
 * Pending AMC services, the whole point of the day settings.
 *
 * Every row is already banded server-side (sp_app_amc_service_due_list) from
 * Amc.ServiceDueLeadDays and Amc.ServiceOverdueCriticalDays, resolved for that row's own
 * branch - so the colour on screen and the number an admin typed on the Settings page are
 * the same fact, and a branch that runs a different rhythm colours differently without any
 * special case here.
 *
 * `daysPending` is signed: negative reads "in N days", zero "due today", positive "N days
 * late". One field drives both the wording and the strip colour, so the two can never
 * disagree with each other.
 *
 * Unlike most widgets in the registry this one renders even when it has nothing - "no AMC
 * service is pending" is the answer an admin opens a dashboard to get, and a card that
 * vanishes on a good day looks identical to a card that was never built.
 */
const AmcServiceDueCard = () => {
  const canView = useHasPermission("Amc.ManageContracts");
  const { selectedBranchId } = useBranchFilter();
  const [showAll, setShowAll] = useState(false);
  // "Raise service visit" goes through the AMC endpoint (POST /amc/schedule/{guid}/create-visit),
  // which creates the job AND links it to the visit in one call. It used to open the ordinary
  // Log Service Visit modal with the AMC brief typed into the description - that made a job
  // the schedule never heard about, so five visits sat here as "pending" on 2026-09-21 while
  // technicians were already assigned to them. Same permission as this card: Amc.ManageContracts.
  const [createVisit, { isLoading: isRaising }] = useCreateVisitFromScheduleMutation();
  const [raisingGuid, setRaisingGuid] = useState(null);

  const handleRaiseVisit = async (row) => {
    if (!window.confirm(`Raise the service visit for ${row.customerName} (${row.contractNo}, visit ${row.visitNumber})?`)) return;
    setRaisingGuid(row.scheduleRecordGuid);
    try {
      const result = await createVisit({
        recordGuid: row.scheduleRecordGuid, visitDescription: visitBrief(row), technicianRecordGuids: null,
      }).unwrap();
      toast.success(`Service visit ${result?.workNumber || ""} raised - assign a technician from Technical Work.`);
    } catch (e) {
      toast.error(e?.message || "Could not raise the service visit");
    } finally {
      setRaisingGuid(null);
    }
  };

  const { data: visitRows = [], isFetching } = useGetAmcServiceDueQuery(
    { branchId: selectedBranchId ?? undefined },
    { skip: !canView }
  );
  const rows = useMemo(() => toContractRows(visitRows), [visitRows]);
  const withTechnician = useMemo(
    () => new Set(visitRows.map((r) => r.contractRecordGuid)).size - rows.length,
    [visitRows, rows]
  );

  if (!canView) return null;

  const visible = showAll ? rows : rows.slice(0, INITIAL_ROWS);
  // Every row carries the settings that applied to it; with mixed branches on screen the
  // first row's values are the honest thing to caption with, so say whose they are.
  const settings = rows[0];

  return (
    <div className="col-12">
      <DashboardSectionCard
        title="AMC - Services Pending"
        headerRight={
          settings
            ? `From ${settings.leadDays} days ahead · critical after ${settings.criticalDays} days`
            : "Set the day thresholds in System Settings"
        }
        isLoading={isFetching}
        isEmpty={rows.length === 0}
        emptyText={
          withTechnician > 0
            ? `Nothing is waiting on the office. ${withTechnician} AMC service(s) are already with a technician.`
            : "No AMC service is pending. Nothing is overdue and nothing falls inside the reminder window."
        }
      >
        <UrgencySummary rows={rows} field="urgency" order={["CRITICAL", "OVERDUE", "DUE", "UPCOMING"]} />

        <div className="d-flex flex-column gap-2">
          {visible.map((row) => (
            <ServiceDueRow
              key={row.scheduleRecordGuid}
              row={row}
              onRaiseVisit={() => handleRaiseVisit(row)}
              isRaising={isRaising && raisingGuid === row.scheduleRecordGuid}
            />
          ))}
        </div>

        {withTechnician > 0 && rows.length > 0 && (
          <div className="text-muted small mt-2">
            {withTechnician} more AMC service(s) are already with a technician and are not listed here.
          </div>
        )}

        {rows.length > INITIAL_ROWS && (
          <button
            type="button"
            className="btn btn-link btn-sm px-0 mt-2"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show less" : `Show all ${rows.length}`}
          </button>
        )}
      </DashboardSectionCard>
    </div>
  );
};

const visitBrief = (row) =>
  `AMC ${row.contractNo} - visit ${row.visitNumber} of the ${String(row.frequency || "").toLowerCase()} schedule, planned ${fmtDate(row.plannedDate)}.`;

// "153 days late" / "Due today" / "in 5 days" - the headline number, and the only
// thing on the row anyone reads first.
const pendingLabel = (days) => {
  if (days < 0) return { big: `in ${Math.abs(days)}`, small: Math.abs(days) === 1 ? "day" : "days" };
  if (days === 0) return { big: "Due", small: "today" };
  return { big: String(days), small: days === 1 ? "day late" : "days late" };
};

const ServiceDueRow = ({ row, onRaiseVisit, isRaising }) => {
  const band = URGENCY[row.urgency] || URGENCY.OVERDUE;
  const label = pendingLabel(row.daysPending);

  return (
    <div className="d-flex align-items-stretch" style={{ gap: 12 }}>
      <UrgencyStrip color={band.color} />

      <div className="flex-grow-1 py-1" style={{ minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" style={{ fontSize: 15 }}>
              {row.customerName || `Customer ${row.custId}`}
            </div>
            <div className="text-muted small text-truncate">
              {row.contractNo} · {row.branchCode} · Visit {row.visitNumber} · planned {fmtDate(row.plannedDate)}
            </div>
            {row.otherVisits?.length > 0 && (
              <div className="small text-truncate" style={{ color: band.color }}>
                +{row.otherVisits.length} more visit{row.otherVisits.length === 1 ? "" : "s"} pending on this contract:{" "}
                {row.otherVisits.map((v) => `visit ${v.visitNumber} (${fmtDate(v.plannedDate)})`).join(", ")}
              </div>
            )}
            <div className="small text-truncate" style={{ color: band.color }}>
              {row.lastServiceDate
                ? `Last serviced ${fmtDate(row.lastServiceDate)} - ${row.daysSinceLastService} days ago`
                : `Never serviced - contract running since ${fmtDate(row.contractStartDate)} (${row.daysSinceLastService} days)`}
            </div>
          </div>

          <div className="text-end flex-shrink-0">
            <div className="fw-bold lh-1" style={{ color: band.color, fontSize: 22 }}>
              {label.big}
            </div>
            <div className="small" style={{ color: band.color }}>{label.small}</div>
          </div>
        </div>

        {/* The row is a call to action, so it carries the two ways out of it: open the
            contract this visit belongs to, or raise the job that settles it. A job
            already raised is not the same as a site already serviced, so the row stays
            on the list and links to the job carrying it instead of offering a second. */}
        <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
          {row.workNumber ? (
            <Link
              to={row.technicalWorkRecordGuid ? paths.technicalWorkDetail(row.technicalWorkRecordGuid) : paths.technicalWork}
              className="badge badge-lineinfo text-decoration-none"
            >
              <Tool size={11} className="me-1" />
              {row.workNumber} · {row.technicalWorkStatus}
            </Link>
          ) : (
            onRaiseVisit && (
              <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2" onClick={onRaiseVisit} disabled={isRaising}>
                <PlusCircle size={12} className="me-1" /> {isRaising ? "Raising…" : "Raise service visit"}
              </button>
            )
          )}
          <Link to={paths.amcRecord(row.contractRecordGuid)} className="btn btn-sm btn-link py-0 px-1">
            <FileText size={12} className="me-1" /> Open AMC
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AmcServiceDueCard;

